'use client'

import { ExternalLink } from 'lucide-react'
import { sanitizeHref } from '@/lib/sanitize'

const GRADE_COLORS: Record<string, string> = {
  A: '#22c55e',
  B: '#008dcc',
  C: '#eab308',
  D: '#f97316',
  F: '#F90B8A',
}

export interface YouTubeMonitorResult {
  brand: string
  keywords: string[]
  checkedAt: string
  totalMentions: number
  totalViews: number
  uniqueChannels: number
  overallScore: number
  grade: 'A' | 'B' | 'C' | 'D' | 'F'
  videos: {
    videoId: string
    title: string
    description: string
    channelTitle: string
    channelId: string
    publishedAt: string
    thumbnailUrl: string
    viewCount: number
    likeCount: number
    commentCount: number
    durationSeconds: number
    url: string
    sentiment: 'positive' | 'neutral' | 'negative'
    sentimentSource: 'transcript' | 'metadata'
    keyClaim: string | null
    brandDensity: number
  }[]
  channelBreakdown: {
    channelTitle: string
    channelId: string
    videoCount: number
    totalViews: number
    avgSentiment: 'positive' | 'neutral' | 'negative'
  }[]
  scoreBreakdown: {
    category: string
    score: number
    maxScore: number
    details: string
  }[]
  sentimentMatrix: { positive: number; neutral: number; negative: number }
  transcriptCoverage: {
    attempted: number
    succeeded: number
    failed: number
  }
  recommendations: {
    priority: 'high' | 'medium' | 'low'
    category: string
    message: string
  }[]
  aiSummary: { summary: string; actions: string[]; context: string } | null
}

export function formatViews(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`
  return n.toString()
}

export function formatRelative(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  if (!Number.isFinite(ms) || ms < 0) return ''
  const days = Math.floor(ms / 86400000)
  if (days < 1) return 'today'
  if (days < 30) return `${days}d ago`
  if (days < 365) return `${Math.floor(days / 30)}mo ago`
  return `${Math.floor(days / 365)}y ago`
}

export default function YouTubeResults({
  result,
}: {
  result: YouTubeMonitorResult
}) {
  const gradeColor = GRADE_COLORS[result.grade]

  return (
    <div className='space-y-6'>
      {/* Score card */}
      <div className='bg-muted/50 rounded-2xl border border-border/60 p-6'>
        <div className='flex flex-col items-center gap-4 py-4'>
          <div
            className='w-32 h-32 rounded-full flex items-center justify-center'
            style={{
              background: `${gradeColor}15`,
              border: `4px solid ${gradeColor}`,
            }}
          >
            <div className='text-center'>
              <div
                className='text-4xl font-black leading-none'
                style={{ color: gradeColor }}
              >
                {result.overallScore}
              </div>
              <div className='text-xs text-muted-foreground mt-1'>/ 100</div>
            </div>
          </div>
          <div className='text-center'>
            <div className='text-3xl font-black' style={{ color: gradeColor }}>
              Grade {result.grade}
            </div>
            <p className='text-sm text-muted-foreground mt-1'>
              {result.totalMentions} videos · {formatViews(result.totalViews)}{' '}
              total views · {result.uniqueChannels} channels
            </p>
            {result.transcriptCoverage.attempted > 0 && (
              <p className='text-xs text-muted-foreground mt-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#8b5cf6]/10 border border-[#8b5cf6]/20'>
                <span className='w-1.5 h-1.5 rounded-full bg-[#8b5cf6]' />
                Sentiment grounded in {
                  result.transcriptCoverage.succeeded
                } of {result.transcriptCoverage.attempted} video transcripts
                (Layer 2)
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Score breakdown */}
      <div className='bg-background border border-border/60 rounded-2xl p-6'>
        <h3 className='text-base font-semibold text-foreground mb-4'>
          Score Breakdown
        </h3>
        <div className='space-y-3'>
          {result.scoreBreakdown.map(s => {
            const pct = (s.score / s.maxScore) * 100
            return (
              <div key={s.category}>
                <div className='flex items-baseline justify-between mb-1'>
                  <span className='text-sm font-medium text-foreground'>
                    {s.category}
                  </span>
                  <span className='text-xs font-mono text-muted-foreground'>
                    {s.score} / {s.maxScore}
                  </span>
                </div>
                <div className='h-2 bg-muted rounded-full overflow-hidden mb-1'>
                  <div
                    className='h-full rounded-full transition-all'
                    style={{
                      width: `${pct}%`,
                      backgroundColor:
                        pct >= 75
                          ? '#22c55e'
                          : pct >= 50
                            ? '#008dcc'
                            : pct >= 25
                              ? '#f97316'
                              : '#F90B8A',
                    }}
                  />
                </div>
                <p className='text-xs text-muted-foreground'>{s.details}</p>
              </div>
            )
          })}
        </div>
      </div>

      {/* AI summary */}
      {result.aiSummary && (
        <div className='bg-[#008dcc]/5 border border-[#008dcc]/20 rounded-2xl p-6'>
          <h3 className='text-base font-semibold text-foreground mb-2'>
            AI Summary
          </h3>
          <p className='text-sm text-foreground/80 leading-relaxed mb-4'>
            {result.aiSummary.summary}
          </p>
          {result.aiSummary.actions.length > 0 && (
            <>
              <p className='text-xs font-semibold text-foreground/70 uppercase tracking-wider mb-2'>
                Next Actions
              </p>
              <ul className='space-y-2'>
                {result.aiSummary.actions.map((a, i) => (
                  <li key={i} className='flex gap-2 text-sm text-foreground/80'>
                    <span className='text-[#008dcc] font-bold flex-shrink-0'>
                      {i + 1}.
                    </span>
                    <span>{a}</span>
                  </li>
                ))}
              </ul>
            </>
          )}
          {result.aiSummary.context && (
            <p className='text-xs text-muted-foreground mt-4 pt-4 border-t border-border/60'>
              {result.aiSummary.context}
            </p>
          )}
        </div>
      )}

      {/* Channel breakdown */}
      {result.channelBreakdown.length > 0 && (
        <div className='bg-background border border-border/60 rounded-2xl p-6'>
          <h3 className='text-base font-semibold text-foreground mb-4'>
            Top Channels ({result.channelBreakdown.length})
          </h3>
          <div className='space-y-2'>
            {result.channelBreakdown.slice(0, 10).map(c => (
              <div
                key={c.channelId}
                className='flex items-center justify-between gap-3 p-3 rounded-lg bg-muted/30'
              >
                <div className='flex-1 min-w-0'>
                  <p className='text-sm font-medium text-foreground truncate'>
                    {c.channelTitle}
                  </p>
                  <p className='text-xs text-muted-foreground'>
                    {c.videoCount} {c.videoCount === 1 ? 'video' : 'videos'} ·{' '}
                    {formatViews(c.totalViews)} views
                  </p>
                </div>
                <span
                  className='text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0'
                  style={{
                    color:
                      c.avgSentiment === 'positive'
                        ? '#22c55e'
                        : c.avgSentiment === 'negative'
                          ? '#F90B8A'
                          : '#6b7280',
                    backgroundColor:
                      c.avgSentiment === 'positive'
                        ? '#22c55e15'
                        : c.avgSentiment === 'negative'
                          ? '#F90B8A15'
                          : '#6b728015',
                  }}
                >
                  {c.avgSentiment}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Top videos */}
      {result.videos.length > 0 && (
        <div className='bg-background border border-border/60 rounded-2xl p-6'>
          <h3 className='text-base font-semibold text-foreground mb-4'>
            Top Videos ({result.videos.length})
          </h3>
          <div className='space-y-3'>
            {result.videos.slice(0, 12).map(v => {
              const safeUrl = sanitizeHref(v.url, {
                allowedSchemes: ['https:'],
              })
              if (!safeUrl) return null
              return (
                <a
                  key={v.videoId}
                  href={safeUrl}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='flex gap-3 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group'
                >
                  {v.thumbnailUrl && (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={v.thumbnailUrl}
                      alt=''
                      className='w-32 h-auto rounded-md flex-shrink-0 object-cover'
                    />
                  )}
                  <div className='flex-1 min-w-0'>
                    <p className='text-sm font-medium text-foreground line-clamp-2 group-hover:text-[#008dcc] transition-colors'>
                      {v.title}
                    </p>
                    <p className='text-xs text-muted-foreground mt-1'>
                      {v.channelTitle} · {formatViews(v.viewCount)} views ·{' '}
                      {formatRelative(v.publishedAt)}
                    </p>
                    {v.keyClaim && (
                      <p className='text-xs text-foreground/70 mt-1.5 italic line-clamp-2 border-l-2 border-[#8b5cf6]/40 pl-2'>
                        &ldquo;{v.keyClaim}&rdquo;
                      </p>
                    )}
                    <div className='flex items-center gap-2 mt-1.5'>
                      <span
                        className='text-[10px] font-semibold uppercase tracking-wider'
                        style={{
                          color:
                            v.sentiment === 'positive'
                              ? '#22c55e'
                              : v.sentiment === 'negative'
                                ? '#F90B8A'
                                : '#6b7280',
                        }}
                      >
                        {v.sentiment}
                      </span>
                      <span
                        className='text-[9px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded'
                        style={{
                          color:
                            v.sentimentSource === 'transcript'
                              ? '#8b5cf6'
                              : '#6b7280',
                          backgroundColor:
                            v.sentimentSource === 'transcript'
                              ? '#8b5cf615'
                              : '#6b728015',
                        }}
                        title={
                          v.sentimentSource === 'transcript'
                            ? 'Sentiment analyzed from the full video transcript'
                            : 'Sentiment inferred from title and description only'
                        }
                      >
                        {v.sentimentSource === 'transcript'
                          ? 'transcript'
                          : 'metadata'}
                      </span>
                      <ExternalLink
                        size={12}
                        className='text-muted-foreground'
                      />
                    </div>
                  </div>
                </a>
              )
            })}
          </div>
        </div>
      )}

      {/* Recommendations */}
      {result.recommendations.length > 0 && (
        <div className='bg-background border border-border/60 rounded-2xl p-6'>
          <h3 className='text-base font-semibold text-foreground mb-4'>
            Recommendations
          </h3>
          <div className='space-y-3'>
            {result.recommendations.map((r, i) => (
              <div
                key={i}
                className='flex gap-3 p-4 rounded-lg border border-border/60 bg-muted/30'
              >
                <span
                  className='text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full flex-shrink-0 h-fit'
                  style={{
                    color:
                      r.priority === 'high'
                        ? '#F90B8A'
                        : r.priority === 'medium'
                          ? '#f97316'
                          : '#6b7280',
                    backgroundColor:
                      r.priority === 'high'
                        ? '#F90B8A15'
                        : r.priority === 'medium'
                          ? '#f9731615'
                          : '#6b728015',
                  }}
                >
                  {r.priority}
                </span>
                <div className='flex-1'>
                  <p className='text-sm font-semibold text-foreground mb-1'>
                    {r.category}
                  </p>
                  <p className='text-sm text-foreground/70'>{r.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}
